import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { MessageView, PublicAccount, ThreadView } from '../lib/api';
import { useStore } from '../lib/store';
import {
  absoluteTimestamp,
  domainOf,
  formatParticipants,
  formatTimestamp,
  hueForAddress,
  initialsFor,
} from '../lib/format';
import { AttachmentStrip } from './AttachmentStrip';
import {
  MessageBody,
  readOriginalDomains,
  useMessageTheme,
  writeOriginalDomains,
} from './MessageBody';
import styles from './ReadingPane.module.css';

/** A thread this long is where a summary starts paying for its own pixels. */
const AI_SUMMARY_MIN_MESSAGES = 4;
const UNREAD_SCROLL_OFFSET = 40;

export type ReplyMode = 'reply' | 'replyAll' | 'forward';

export interface ReadingPaneProps {
  /** Omit to follow `store.selectedThreadKey`; pass `null` to force the empty state. */
  threadKey?: string | null;
  aiEnabled?: boolean;
  /** Sender domains the user has already trusted with remote images. */
  allowedImageDomains?: string[];
  onAllowImageDomain?: (domain: string) => void;
  onReply?: (message: MessageView, mode: ReplyMode) => void;
  onArchive?: (threadKey: string) => void;
  onSnooze?: (threadKey: string) => void;
  onMove?: (threadKey: string) => void;
  onLabel?: (threadKey: string) => void;
  onMore?: (threadKey: string, anchor: HTMLElement) => void;
  className?: string;
}

function cssVars(vars: Record<string, string | number>): CSSProperties {
  return vars as unknown as CSSProperties;
}

function ArchiveIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M3 5.4h14M4.6 5.4v9.8h10.8V5.4M8 9.4 10 11.4l2-2"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.3" />
      <path d="M10 6v4.2l2.6 1.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M3 6.2A1.2 1.2 0 0 1 4.2 5h3l1.6 1.8h7A1.2 1.2 0 0 1 17 8v6.4a1.2 1.2 0 0 1-1.2 1.2H4.2A1.2 1.2 0 0 1 3 14.4z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M3.6 9.6V4.2A.6.6 0 0 1 4.2 3.6h5.4l7 7-6 6z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <circle cx="6.9" cy="6.9" r="1.1" fill="currentColor" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="10" r="1.4" />
      <circle cx="10" cy="10" r="1.4" />
      <circle cx="15" cy="10" r="1.4" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={styles.chevronIcon}
      data-open={open}
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path d="m3.6 5.4 3.4 3.4 3.4-3.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ReplyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M6.4 3.6 2.8 6.9l3.6 3.3M3.2 6.9h5.4a4 4 0 0 1 4 4v1.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ReplyAllIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M5.6 3.6 2 6.9l3.6 3.3M8.6 3.6 5 6.9l3.6 3.3M5.6 6.9h4.2a4 4 0 0 1 4 4v1.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ForwardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M9.6 3.6 13.2 6.9l-3.6 3.3M12.8 6.9H7.4a4 4 0 0 0-4 4v1.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The Inbox Zero mark.
 *
 * Superhuman rewards a cleared inbox with a full-bleed photograph and hides all
 * chrome — it is the most-loved thing in the product. A remote image is out of
 * the question here (the CSP forbids it and the app has to work offline), so
 * the reward is built from a generated gradient and one thin-stroke drawing:
 * a horizon with a sun just above it. Stroke only, currentColor only, so it
 * costs nothing and inverts correctly in both themes.
 */
function HorizonMark() {
  return (
    <svg
      className={styles.zeroMark}
      viewBox="0 0 220 120"
      fill="none"
      role="img"
      aria-label="A clear horizon"
    >
      <circle cx="110" cy="66" r="26" stroke="currentColor" strokeWidth="1.25" opacity="0.85" />
      <path d="M8 92h204" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      <path d="M28 78h44" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" opacity="0.5" />
      <path d="M148 78h44" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" opacity="0.5" />
      <path d="M52 104h116" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" opacity="0.3" />
    </svg>
  );
}

function ClipIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M9.9 4.1 5.2 8.8a1.4 1.4 0 0 0 2 2l4.6-4.7a2.8 2.8 0 0 0-4-4L3.2 6.9a4.2 4.2 0 0 0 6 6L13 9"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Avatar({ person, size }: { person: { name: string; address: string }; size: 24 | 36 }) {
  return (
    <span
      className={`${styles.avatar} ${size === 24 ? styles.avatar24 : styles.avatar36}`}
      style={cssVars({ '--h': hueForAddress(person.address) })}
      aria-hidden="true"
    >
      {initialsFor(person.name || person.address)}
    </span>
  );
}

function addressList(people: { name: string; address: string }[]): string {
  if (people.length === 0) return '—';
  return people.map((p) => (p.name ? `${p.name} <${p.address}>` : p.address)).join(', ');
}

/** One chord, rendered as chips: 'g i' becomes two. */
function Keys({ chord, label }: { chord: string; label: string }) {
  return (
    <span className="kiln-kbd-run">
      {chord.split(' ').map((step, i) => (
        <kbd key={`${step}-${i}`} className="kiln-kbd">
          {step}
        </kbd>
      ))}
      <span className={styles.keyLabel}>{label}</span>
    </span>
  );
}

export function ReadingPane({
  threadKey,
  aiEnabled = false,
  allowedImageDomains,
  onAllowImageDomain,
  onReply,
  onArchive,
  onSnooze,
  onMove,
  onLabel,
  onMore,
  className,
}: ReadingPaneProps) {
  const qc = useQueryClient();
  const storeKey = useStore((s) => s.selectedThreadKey);
  const listEmpty = useStore((s) => s.listEmpty);
  const showAiSurfaces = useStore((s) => s.appearance.showAi);
  const autoLoadImages = useStore((s) => s.appearance.autoLoadImages);
  const key = threadKey !== undefined ? threadKey : storeKey;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Keyed by message id. The elements are <article>, so the value type is
  // HTMLElement rather than HTMLDivElement.
  const messageRefs = useRef(new Map<number, HTMLElement>());
  const initedKeyRef = useRef<string | null>(null);
  const scrolledKeyRef = useRef<string | null>(null);

  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set());
  const [details, setDetails] = useState<ReadonlySet<number>>(() => new Set());
  const [now, setNow] = useState(() => Date.now());

  // Null in the light theme, where nothing is transformed and the escape hatch
  // has nothing to escape from.
  const darkPalette = useMessageTheme();
  const [originalDomains, setOriginalDomains] = useState<string[]>(readOriginalDomains);

  const threadQuery = useQuery({
    queryKey: ['thread', key],
    queryFn: () => api.thread(key as string),
    enabled: Boolean(key),
  });

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts() });
  const accounts: PublicAccount[] = accountsQuery.data?.accounts ?? [];

  const thread: ThreadView | undefined = threadQuery.data?.thread;
  const messages: MessageView[] = useMemo(() => threadQuery.data?.messages ?? [], [threadQuery.data]);

  const summaryQuery = useQuery({
    queryKey: ['ai', 'summary', key],
    queryFn: () => api.ai.summarize(key as string),
    enabled: Boolean(
      aiEnabled && key && messages.length >= AI_SUMMARY_MIN_MESSAGES && !thread?.aiSummary,
    ),
  });

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Expansion is decided once per thread; a background refetch must not fold
  // a message the reader just opened.
  useEffect(() => {
    if (!key || messages.length === 0 || initedKeyRef.current === key) return;
    initedKeyRef.current = key;
    const next = new Set<number>();
    for (const m of messages) if (!m.seen) next.add(m.id);
    const last = messages[messages.length - 1];
    if (last) next.add(last.id);
    setExpanded(next);
    setDetails(new Set());
  }, [key, messages]);

  useLayoutEffect(() => {
    if (!key || messages.length === 0) return;
    if (initedKeyRef.current !== key || scrolledKeyRef.current === key) return;
    scrolledKeyRef.current = key;

    const scroller = scrollRef.current;
    if (!scroller) return;
    const firstUnread = messages.find((m) => !m.seen);
    if (!firstUnread) {
      scroller.scrollTop = 0;
      return;
    }
    const frame = requestAnimationFrame(() => {
      const target = messageRefs.current.get(firstUnread.id);
      if (!target) return;
      const delta = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      scroller.scrollTop = Math.max(0, scroller.scrollTop + delta - UNREAD_SCROLL_OFFSET);
    });
    return () => cancelAnimationFrame(frame);
  }, [key, messages]);

  const toggleExpanded = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleDetails = useCallback((id: number) => {
    setDetails((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const runFallback = useCallback(
    (action: 'archive' | 'snooze', threadKeyValue: string) => {
      const body =
        action === 'snooze' ? { until: Math.floor(Date.now() / 1000) + 60 * 60 * 24 } : undefined;
      void api.threadAction(threadKeyValue, action, body).finally(() => {
        void qc.invalidateQueries({ queryKey: ['threads'] });
        void qc.invalidateQueries({ queryKey: ['thread', threadKeyValue] });
      });
    },
    [qc],
  );

  const selfAddresses = useMemo(
    () =>
      accounts.flatMap((a) =>
        [a.email, a.sendAs].filter((v): v is string => Boolean(v)).map((v) => v.toLowerCase()),
      ),
    [accounts],
  );

  const allowedDomains = useMemo(
    () => new Set((allowedImageDomains ?? []).map((d) => d.toLowerCase())),
    [allowedImageDomains],
  );

  const originalSet = useMemo(() => new Set(originalDomains), [originalDomains]);

  /**
   * The choice is stored per sender domain, not per message: a sender whose
   * HTML does not survive the transform will not survive it next week either,
   * and asking the reader to re-toggle every message would be the actual bug.
   */
  const toggleOriginal = useCallback((domain: string) => {
    if (!domain) return;
    setOriginalDomains((prev) => {
      const next = prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain];
      writeOriginalDomains(next);
      return next;
    });
  }, []);

  if (!key) {
    // Nothing selected *and* nothing left to select is the moment worth marking.
    if (listEmpty) {
      return (
        <div className={`${styles.pane} ${styles.zeroPane} ${className ?? ''}`}>
          <div className={styles.zero}>
            <HorizonMark />
            <h2 className={styles.zeroTitle}>Inbox zero</h2>
            <p className={styles.zeroBody}>
              Everything is triaged. Nothing here needs you right now.
            </p>
            <div className={styles.zeroKeys}>
              <Keys chord="c" label="compose" />
              <Keys chord="/" label="search" />
              <Keys chord="g e" label="done" />
              <Keys chord="?" label="all shortcuts" />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`${styles.pane} ${className ?? ''}`}>
        <div className={styles.centered}>
          <div className={styles.centeredInner}>
            <h2 className={styles.centeredTitle}>Nothing selected</h2>
            <p className={styles.centeredBody}>Pick a conversation from the list.</p>
            <div className={styles.zeroKeys}>
              <Keys chord="j k" label="move" />
              <Keys chord="↵" label="open" />
              <Keys chord="e" label="archive" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (threadQuery.isError) {
    return (
      <div className={`${styles.pane} ${className ?? ''}`}>
        <div className={styles.centered}>
          <div className={styles.centeredInner}>
            <h2 className={styles.centeredTitle}>Could not open this thread</h2>
            <p className={styles.centeredBody}>
              The server did not answer. The messages themselves are untouched.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!thread) {
    return <div className={`${styles.pane} ${className ?? ''}`} aria-busy="true" />;
  }

  const threadAccounts = thread.accountIds
    .map((id) => accounts.find((a) => a.id === id))
    .filter((a): a is PublicAccount => Boolean(a));

  const summary = thread.aiSummary ?? summaryQuery.data?.summary ?? null;
  const showAiSlot = aiEnabled && showAiSurfaces && messages.length >= AI_SUMMARY_MIN_MESSAGES;

  return (
    <div className={`${styles.pane} ${className ?? ''}`}>
      <header className={styles.header}>
        <div className={styles.headerMain}>
          <h1 className={styles.subject} title={thread.subject}>
            {thread.subject || '(no subject)'}
          </h1>
          <div className={styles.subRow}>
            {threadAccounts.map((account) => (
              <span key={account.id} className={styles.accountPill} title={account.email}>
                <span className={styles.accountDot} style={cssVars({ '--dot-color': account.color })} />
                {account.email}
              </span>
            ))}
            {thread.category && <span className={styles.chip}>{thread.category}</span>}
            <span>
              {messages.length} {messages.length === 1 ? 'message' : 'messages'}
            </span>
          </div>
        </div>

        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.iconBtn}
            title="Archive · e"
            aria-label="Archive"
            onClick={() => (onArchive ? onArchive(thread.threadKey) : runFallback('archive', thread.threadKey))}
          >
            <ArchiveIcon />
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            title="Snooze · h"
            aria-label="Snooze"
            onClick={() => (onSnooze ? onSnooze(thread.threadKey) : runFallback('snooze', thread.threadKey))}
          >
            <ClockIcon />
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            title="Move"
            aria-label="Move"
            onClick={() => onMove?.(thread.threadKey)}
          >
            <FolderIcon />
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            title="Label"
            aria-label="Label"
            onClick={() => onLabel?.(thread.threadKey)}
          >
            <TagIcon />
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            title="More actions"
            aria-label="More actions"
            onClick={(e) => onMore?.(thread.threadKey, e.currentTarget)}
          >
            <MoreIcon />
          </button>
        </div>
      </header>

      {showAiSlot && (
        <div className={styles.aiSlot}>
          <span className={styles.aiLabel}>Summary</span>
          <span className={styles.aiText}>
            {summary ?? (summaryQuery.isFetching ? 'Reading the thread…' : '')}
          </span>
        </div>
      )}

      <div className={styles.scroll} ref={scrollRef}>
        <div className={styles.column}>
          {messages.map((message) => {
            const isOpen = expanded.has(message.id);
            const time = formatTimestamp(message.date, now);
            const timeTitle = absoluteTimestamp(message.date);
            const domain = domainOf(message.from.address);

            return (
              <article
                key={message.id}
                className={styles.message}
                ref={(el) => {
                  if (el) messageRefs.current.set(message.id, el);
                  else messageRefs.current.delete(message.id);
                }}
              >
                {!isOpen ? (
                  <button
                    type="button"
                    className={styles.collapsed}
                    onClick={() => toggleExpanded(message.id)}
                    aria-expanded={false}
                  >
                    <Avatar person={message.from} size={24} />
                    <span className={styles.collapsedSender}>
                      {message.from.name || message.from.address}
                    </span>
                    <span className={styles.collapsedSnippet}>{message.snippet}</span>
                    <span className={styles.collapsedMeta}>
                      {!message.seen && <span className={styles.unreadDot} aria-label="Unread" />}
                      {message.attachments.length > 0 && <ClipIcon />}
                      <span title={timeTitle}>{time}</span>
                    </span>
                  </button>
                ) : (
                  <div className={styles.expanded}>
                    <div className={styles.msgHeader}>
                      <Avatar person={message.from} size={36} />
                      <div className={styles.msgWho}>
                        <div className={styles.msgName}>
                          <span className={styles.msgNameText}>
                            {message.from.name || message.from.address}
                          </span>
                          {message.from.name && (
                            <span className={styles.msgAddress}>{message.from.address}</span>
                          )}
                        </div>
                        <div className={styles.msgTo}>
                          <span>to {formatParticipants(message.to, selfAddresses)}</span>
                          <button
                            type="button"
                            className={styles.chevron}
                            aria-label="Message details"
                            aria-expanded={details.has(message.id)}
                            onClick={() => toggleDetails(message.id)}
                          >
                            <ChevronIcon open={details.has(message.id)} />
                          </button>
                        </div>
                      </div>

                      {darkPalette && message.bodyHtml && domain && (
                        <button
                          type="button"
                          className={styles.msgOriginal}
                          aria-pressed={originalSet.has(domain)}
                          title={
                            originalSet.has(domain)
                              ? `Showing the sender's own colours for ${domain}. Click to match the app theme.`
                              : `Adapted to the dark theme. Click to show ${domain} exactly as sent.`
                          }
                          onClick={() => toggleOriginal(domain)}
                        >
                          {originalSet.has(domain) ? 'Original' : 'Adapted'}
                        </button>
                      )}

                      <div className={styles.msgActions}>
                        <button
                          type="button"
                          className={styles.iconBtn}
                          title="Reply · r"
                          aria-label="Reply"
                          onClick={() => onReply?.(message, 'reply')}
                        >
                          <ReplyIcon />
                        </button>
                        <button
                          type="button"
                          className={styles.iconBtn}
                          title="Reply all · a"
                          aria-label="Reply all"
                          onClick={() => onReply?.(message, 'replyAll')}
                        >
                          <ReplyAllIcon />
                        </button>
                        <button
                          type="button"
                          className={styles.iconBtn}
                          title="Forward · f"
                          aria-label="Forward"
                          onClick={() => onReply?.(message, 'forward')}
                        >
                          <ForwardIcon />
                        </button>
                      </div>

                      <div className={styles.msgMeta}>
                        <span title={timeTitle}>{time}</span>
                        <button
                          type="button"
                          className={styles.chevron}
                          aria-label="Collapse message"
                          onClick={() => toggleExpanded(message.id)}
                        >
                          <ChevronIcon open />
                        </button>
                      </div>
                    </div>

                    {details.has(message.id) && (
                      <div className={styles.details}>
                        <span className={styles.detailKey}>From</span>
                        <span className={styles.detailValue}>{addressList([message.from])}</span>
                        <span className={styles.detailKey}>To</span>
                        <span className={styles.detailValue}>{addressList(message.to)}</span>
                        {message.cc.length > 0 && (
                          <>
                            <span className={styles.detailKey}>Cc</span>
                            <span className={styles.detailValue}>{addressList(message.cc)}</span>
                          </>
                        )}
                        <span className={styles.detailKey}>Date</span>
                        <span className={styles.detailValue}>{absoluteTimestamp(message.date)}</span>
                      </div>
                    )}

                    <MessageBody
                      className={styles.body}
                      message={message}
                      allowRemoteImages={allowedDomains.has(domain)}
                      autoLoadImages={autoLoadImages}
                      onAllowDomain={onAllowImageDomain}
                      original={originalSet.has(domain)}
                    />

                    {message.attachments.length > 0 && (
                      <AttachmentStrip
                        className={styles.attachments}
                        messageId={message.id}
                        attachments={message.attachments}
                      />
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ReadingPane;
